import React, { useContext, useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import AdminInterestContext from "../../context/adminInterest/adminInterestContext";
import UserContext from "../../context/users/userContext";
import AuthContext from "../../context/auth/authContext";
import moment from "moment";
import * as FaIcons from "react-icons/fa";
import { toast } from "react-toastify";

const EditInterestForm = () => {
  const navigate = useNavigate();
  const { _id } = useParams();
  const adminInterestContext = useContext(AdminInterestContext);
  const userContext = useContext(UserContext);
  const authContext = useContext(AuthContext);
  const { getEntrie, entrie, clearEntrie, updateEntrie } = adminInterestContext;
  const { currentUser, user } = userContext;
  const { user: authUser } = authContext;

  const [ageError, setAgeError] = useState("");
  const [error, setError] = useState("");
  const [error1, setError1] = useState("");
  const [learningCenters, setLearningCenters] = useState([]);
  // ADD: New state variables for charter/site functionality
  const [availableCharters, setAvailableCharters] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedCharter, setSelectedCharter] = useState("");

  const calculateAge = (birthDate) => {
    const birthYear = moment(birthDate).year();
    const currentYear = moment().year();
    return currentYear - birthYear;
  };

  const isEmpty = (value) => {
    return (
      !value ||
      value.trim() === "" ||
      value === "undefined" ||
      value === "null" ||
      value === null ||
      value === undefined
    );
  };

  const wereCharterFieldsEmpty = useCallback((entrie) => {
    if (!entrie) {
      return true;
    }

    return (
      isEmpty(entrie?.learningCenterName) &&
      isEmpty(entrie?.learningCenterCharter) &&
      isEmpty(entrie?.learningCenterAddress)
    );
  }, []);

  // ADD: New function to get charters for user
  const getChartersForUser = useCallback(() => {
    let charters = [];
    const activeUser = authUser || currentUser || user;

    const isSuperAdmin = activeUser?.userStatus === "Super Admin";

    if (isSuperAdmin) {
      // Get unique charters from search sites
      if (adminInterestContext.searchSites) {
        const siteCharters = adminInterestContext.searchSites
          .map(site => site.charter)
          .filter(charter => charter && charter.trim() !== "")
          .filter((charter, index, self) => self.indexOf(charter) === index)
          .map(charter => ({
            id: `site-charter-${charter}`,
            name: charter,
          }));
        charters = [...charters, ...siteCharters];
      }

      // Get charters from userContext
      if (userContext.charters) {
        const contextCharters = userContext.charters.map(charter => ({
          id: charter._id || charter.id,
          name: charter.name,
        }));
        charters = [...charters, ...contextCharters];
      }
    } else {
      
      if (
        activeUser?.charterAssignments &&
        activeUser.charterAssignments.length > 0
      ) {
        charters = activeUser.charterAssignments.map(assignment => ({
          id: assignment._id || assignment.id,
          name: assignment.name || assignment.charter,
        }));
      }
    }

   
    const uniqueCharters = charters.filter(
      (charter, index, self) =>
        index === self.findIndex((c) => c.name === charter.name)
    );

    return uniqueCharters.sort((a, b) => a.name.localeCompare(b.name));
  }, [
    authUser,
    currentUser,
    user,
    adminInterestContext.searchSites,
    userContext.charters,
  ]);

  // New function to get sites for charter
  const getSitesForCharter = useCallback((charterName) => {
    if (!charterName) return [];

    let sites = [];
    const activeUser = authUser || currentUser || user;
    const isSuperAdmin = activeUser?.userStatus === "Super Admin";

    if (isSuperAdmin) {
      if (adminInterestContext.searchSites) {
        sites = adminInterestContext.searchSites
          .filter(site => site.charter === charterName)
          .map((site, index) => ({
            id: site._id || site.id || `site-${index}`,
            name: site.name || `Site ${index + 1}`,
            charter: site.charter || "",
            address: (() => {
              // Find primary address using comprehensive logic
              let primaryAddress = null;
              
              // Step 1: Check if main address is marked as primary
              if (site.address && site.address.isPrimary === true) {
                primaryAddress = site.address;
              }
              
              // Step 2: If no primary main address, check addresses array for primary
              if (!primaryAddress && site.addresses && site.addresses.length > 0) {
                primaryAddress = site.addresses.find(addr => addr.isPrimary === true);
              }
              
              // Step 3: If no primary address found anywhere, use main address as fallback
              if (!primaryAddress && site.address) {
                primaryAddress = site.address;
              }
              
              // Step 4: If still no address, use first address from array
              if (!primaryAddress && site.addresses && site.addresses.length > 0) {
                primaryAddress = site.addresses[0];
              }
              
              // Format the address
              return primaryAddress 
                ? `${primaryAddress.address_1 || ""} ${primaryAddress.city || ""} ${primaryAddress.state || ""}`.trim()
                : "";
            })(),
          }));
      }
    } else {
      if (
        activeUser?.charterAssignments &&
        activeUser.charterAssignments.length > 0
      ) {
        const assignedCharterNames = activeUser.charterAssignments.map(
          (assignment) => assignment.name || assignment.charter
        );

        if (assignedCharterNames.includes(charterName)) {
          if (adminInterestContext.searchSites) {
            sites = adminInterestContext.searchSites
              .filter(site => site.charter === charterName)
              .map((site, index) => ({
                id: site._id || site.id || `site-${index}`,
                name: site.name || `Site ${index + 1}`,
                charter: site.charter || "",
                address: (() => {
                  // Find primary address using comprehensive logic
                  let primaryAddress = null;
                  
                  // Step 1: Check if main address is marked as primary
                  if (site.address && site.address.isPrimary === true) {
                    primaryAddress = site.address;
                  }
                  
                  // Step 2: If no primary main address, check addresses array for primary
                  if (!primaryAddress && site.addresses && site.addresses.length > 0) {
                    primaryAddress = site.addresses.find(addr => addr.isPrimary === true);
                  }
                  
                  // Step 3: If no primary address found anywhere, use main address as fallback
                  if (!primaryAddress && site.address) {
                    primaryAddress = site.address;
                  }
                  
                  // Step 4: If still no address, use first address from array
                  if (!primaryAddress && site.addresses && site.addresses.length > 0) {
                    primaryAddress = site.addresses[0];
                  }
                  
                  // Format the address
                  return primaryAddress 
                    ? `${primaryAddress.address_1 || ""} ${primaryAddress.city || ""} ${primaryAddress.state || ""}`.trim()
                    : "";
                })(),
              }));
          }
        }
      }
    }
return sites.sort((a, b) => a.name.localeCompare(b.name));
  }, [
    authUser,
    currentUser,
    user,
    adminInterestContext.searchSites,
  ]);

  const getLearningCentersForUser = useCallback(() => {
    let centers = [];
    const activeUser = authUser || currentUser || user;
   // console.log("activeUser", activeUser);

    // Check if user is Super Admin
    const isSuperAdmin = activeUser?.userStatus === "Super Admin";

    if (isSuperAdmin) {
      // Super Admin - show ALL learning centers
     // console.log("Super Admin detected - showing all learning centers");

      if (adminInterestContext.searchSites) {
        const allSites = adminInterestContext.searchSites.map(
          (site, index) => ({
            id: site._id || site.id || `site-${index}`,
            name: site.name || `Site ${index + 1}`,
            charter: site.charter || "",
            address: (() => {
              // Find primary address using comprehensive logic
              let primaryAddress = null;
              
              // Step 1: Check if main address is marked as primary
              if (site.address && site.address.isPrimary === true) {
                primaryAddress = site.address;
              }
              
              // Step 2: If no primary main address, check addresses array for primary
              if (!primaryAddress && site.addresses && site.addresses.length > 0) {
                primaryAddress = site.addresses.find(addr => addr.isPrimary === true);
              }
              
              // Step 3: If no primary address found anywhere, use main address as fallback
              if (!primaryAddress && site.address) {
                primaryAddress = site.address;
              }
              
              // Step 4: If still no address, use first address from array
              if (!primaryAddress && site.addresses && site.addresses.length > 0) {
                primaryAddress = site.addresses[0];
              }
              
              // Format the address
              return primaryAddress 
                ? `${primaryAddress.address_1 || ""} ${primaryAddress.city || ""} ${primaryAddress.state || ""}`.trim()
                : "";
            })(),
          })
        );
        centers = [...centers, ...allSites];
      }

      if (userContext.charters) {
        const allCharterCenters = userContext.charters.map(
          (charter, index) => ({
            id: charter._id || charter.id || `charter-${index}`,
            name: charter.name || `Charter ${index + 1}`,
            charter: charter.name || "",
            address: charter.address || charter.location || "",
          })
        );
        centers = [...centers, ...allCharterCenters];
      }
    } else {
      // Regular user - filter based on charter assignments
      if (
        activeUser?.charterAssignments &&
        activeUser.charterAssignments.length > 0
      ) {
        const assignedCharterNames = activeUser.charterAssignments.map(
          (assignment) => assignment.name || assignment.charter
        );
       // console.log("assignedCharterNames", assignedCharterNames);

        if (adminInterestContext.searchSites) {
          const filteredSites = adminInterestContext.searchSites.filter(
            (site) => assignedCharterNames.includes(site.charter)
          );
          centers = filteredSites.map((site, index) => ({
            id: site._id || site.id || `site-${index}`,
            name: site.name || `Site ${index + 1}`,
            charter: site.charter || "",
            address: (() => {
              // Find primary address using comprehensive logic
              let primaryAddress = null;
              
              // Step 1: Check if main address is marked as primary
              if (site.address && site.address.isPrimary === true) {
                primaryAddress = site.address;
              }
              
              // Step 2: If no primary main address, check addresses array for primary
              if (!primaryAddress && site.addresses && site.addresses.length > 0) {
                primaryAddress = site.addresses.find(addr => addr.isPrimary === true);
              }
              
              // Step 3: If no primary address found anywhere, use main address as fallback
              if (!primaryAddress && site.address) {
                primaryAddress = site.address;
              }
              
              // Step 4: If still no address, use first address from array
              if (!primaryAddress && site.addresses && site.addresses.length > 0) {
                primaryAddress = site.addresses[0];
              }
              
              // Format the address
              return primaryAddress 
                ? `${primaryAddress.address_1 || ""} ${primaryAddress.city || ""} ${primaryAddress.state || ""}`.trim()
                : "";
            })(),
          }));
        }

        if (userContext.charters) {
          const assignedCharterIds = activeUser.charterAssignments.map(
            (assignment) => assignment._id
          );
          const filteredCharters = userContext.charters.filter((charter) =>
            assignedCharterIds.includes(charter._id)
          );
          const charterCenters = filteredCharters.map((charter, index) => ({
            id: charter._id || charter.id || `charter-${index}`,
            name: charter.name || `Charter ${index + 1}`,
            charter: charter.name || "",
            address: charter.address || charter.location || "",
          }));
          centers = [...centers, ...charterCenters];
        }
      } else {
        console.log(
          "No charter assignments found for regular user - no centers available"
        );
      }
    }

    // Remove duplicates based on name
    const uniqueCenters = centers.filter(
      (center, index, self) =>
        index === self.findIndex((c) => c.name === center.name)
    );

    return [
      {
        id: "empty",
        name: "",
        charter: "",
        address: "",
        isEmpty: true,
      },
      ...uniqueCenters,
    ];
  }, [
    authUser,
    currentUser,
    user,
    adminInterestContext.searchSites,
    userContext.charters,
  ]);
  const activeUser = authUser || currentUser || user;
  const userRole = activeUser?.userStatus || activeUser?.role;
  const fieldsEmpty = wereCharterFieldsEmpty(entrie);

  let shouldEnableCharterFields = false;
  if (activeUser && activeUser.id) {
    shouldEnableCharterFields = true;
  }

  const [formData, setFormData] = useState({
    studentId: "",
    studentNumber: "",
    studentFirstName: "",
    studentLastName: "",
    gradeLevel: "",
    birthDate: "",
    age: "",
    parentRelationship: "",
    parentFirstName: "",
    parentLastName: "",
    primaryEmail: "",
    phone: "",
    street1: "",
    street2: "",
    state: "",
    region: "",
    city: "",
    country: "",
    zipCode: "",
    sameAddress: false,
    mailingStreet1: "",
    mailingStreet2: "",
    mailingCity: "",
    mailingState: "",
    mailingRegion: "",
    mailingCountry: "",
    mailingZipCode: "",
    learningCenterName: "",
    learningCenterCharter: "",
    learningCenterAddress: "",
    learningCenterId: "",
    brandName:"",
  });

  const alphabetRegex = /^[A-Za-z\s]+$/;

  // ADD: New charter change handler
  const handleCharterChange = (e) => {
    const charterName = e.target.value;
    setSelectedCharter(charterName);
    
    // Reset site selection when charter changes
    setFormData((prevData) => ({
      ...prevData,
      learningCenterName: "",
      learningCenterCharter: charterName,
      learningCenterAddress: "",
      learningCenterId: "",
    }));

    // Update available sites based on selected charter
    setAvailableSites(getSitesForCharter(charterName));
  };

  // ADD: New site change handler
  const handleSiteChange = (e) => {
    const siteName = e.target.value;
    const selectedSite = availableSites.find(site => site.name === siteName);
    
    if (selectedSite) {
      setFormData((prevData) => ({
        ...prevData,
        learningCenterName: selectedSite.name,
        learningCenterAddress: selectedSite.address,
        learningCenterId: selectedSite.id,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        learningCenterName: "",
        learningCenterAddress: "",
        learningCenterId: "",
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const alphabeticFields = [
      "studentFirstName",
      "studentLastName",
      "parentFirstName",
      "parentLastName",
      "parentRelationship",
      "state",
      "region",
      "city",
      "country",
      "mailingState",
      "mailingRegion",
      "mailingCountry",
      "mailingCity",
    ];

    if (type === "checkbox") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: checked,
      }));
      return;
    }

    if (name === "learningCenterName") {
      const selectedCenter = learningCenters.find(
        (center) => center.name === value
      );
      if (selectedCenter && !selectedCenter.isEmpty) {
        setFormData((prevData) => ({
          ...prevData,
          learningCenterName: selectedCenter.name,
          learningCenterCharter: selectedCenter.charter,
          learningCenterAddress: selectedCenter.address,
          learningCenterId: selectedCenter.id,
        }));
        return;
      } else if (value === "" || selectedCenter?.isEmpty) {
        setFormData((prevData) => ({
          ...prevData,
          learningCenterName: "",
          learningCenterCharter: "",
          learningCenterAddress: "",
          learningCenterId: "",
        }));
        return;
      }
    }

    if (name === "zipCode") {
      const numericValue = value.trim();

      if (!/^\d{0,5}$/.test(numericValue)) {
        setError("ZIP code must be 5 digits.");
        return;
      }

      if (value.length < 5) {
        setError("ZIP code must be 5 digits.");
      } else {
        setError("");
      }
      setFormData((prevData) => ({
        ...prevData,
        [name]: numericValue,
      }));
      return;
    }
    if (name === "mailingZipCode") {
      const numericValue = value.trim();

      if (!/^\d{0,5}$/.test(numericValue)) {
        setError1("ZIP code must be 5 digits.");
        return;
      }

      if (value.length < 5) {
        setError1("ZIP code must be 5 digits.");
      } else {
        setError1("");
      }
      setFormData((prevData) => ({
        ...prevData,
        [name]: numericValue,
      }));
      return;
    }

    if (name === "birthDate") {
      const calculatedAge = calculateAge(value);
      setFormData((prevData) => ({
        ...prevData,
        birthDate: value,
        age: calculatedAge,
      }));
      return;
    }

    if (
      alphabeticFields.includes(name) &&
      value !== "" &&
      !alphabetRegex.test(value)
    ) {
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateEmail = (email) => {
    const reg =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return reg.test(String(email).toLowerCase());
  };

  const validatePhone = (number) => {
    const re = /^\d{3}(\))?(-|\s)?\d{3}(-|\s)\d{4}$/;
    return re.test(String(number));
  };

  const formatPhoneNumber = (evt) => {
    let aVal = evt.target.value.replace(/\D/g, "");

    if (aVal.length >= 10 && aVal.charAt(0) === "1") {
      aVal = aVal.substring(1);
    }

    if (aVal.length > 0) {
      if (aVal.length > 6) {
        aVal = `${aVal.substring(0, 3)}-${aVal.substring(
          3,
          6
        )}-${aVal.substring(6, 10)}`;
      } else if (aVal.length > 3) {
        aVal = `${aVal.substring(0, 3)}-${aVal.substring(3, 6)}`;
      } else {
        aVal = aVal;
      }
    }

    evt.target.value = aVal;
  };

  useEffect(() => {
    getEntrie(_id);
    return () => {
      clearEntrie();
    };
  }, []);

 useEffect(() => {
  // Load all data in parallel on mount
  const loadData = async () => {
    const promises = [getEntrie(_id)];
    
    if (userContext.getCharters) {
      promises.push(userContext.getCharters());
    }
    
    if (adminInterestContext.getSearchSites) {
      promises.push(adminInterestContext.getSearchSites());
    }
    
    Promise.all(promises).catch(console.error);
  };
  
  loadData();
  
  return () => {
    clearEntrie();
  };
}, [_id]);

useEffect(() => {
  // Update form data when entry becomes available
  if (entrie) {
    setFormData((prevData) => ({
      ...prevData,
      ...entrie,
    }));
    
    if (entrie.learningCenterCharter) {
      setSelectedCharter(entrie.learningCenterCharter);
      setAvailableSites(getSitesForCharter(entrie.learningCenterCharter));
    }
  }
  
  // Update learning centers and charters when context data changes
  setLearningCenters(getLearningCentersForUser());
  setAvailableCharters(getChartersForUser());
}, [
  entrie,
  adminInterestContext.searchSites,
  userContext.charters,
  authUser,
  currentUser,
  user,
]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateEmail(formData.primaryEmail)) {
      toast.error("Please provide a valid email address.");
     // alert("Please provide a valid email address.");
      return;
    }
    if (!validatePhone(formData.phone)) {
      toast.error("Please provide a valid phone number in the format XXX-XXX-XXXX.");
    //  alert("Please provide a valid phone number in the format XXX-XXX-XXXX.");
      return;
    }
    updateEntrie(_id, formData);
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Edit Interest Form</title>
      </Helmet>
      <h1 className="top-title text-center">Edit Interest Form</h1>
      <div className="top-container">
        <div
          className="right display-back"
          style={{ fontSize: "18px", marginLeft: "40px" }}
          onClick={() => {
            navigate(-1);
          }}
        >
          <FaIcons.FaArrowAltCircleLeft className="back-icon" />
          <h4>Back</h4>
        </div>
      </div>
      <div id="interest-form-page">
        <div className="interest-form">
          <div className="container">
            {entrie ? (
              <form onSubmit={handleSubmit}>
                <div className="sub-forms">
                  <div className="sub-form" style={{ marginTop: "5px" }}>
                    <h3>Selected Learning Center</h3>
                    
                     <div style={{ marginTop: "5px" }}>
                      <h4>Brand Name:</h4>
                      <input
                        type="text"
                        name="brandName"
                        value={formData.brandName}
                        onChange={handleChange}
                        disabled
                        style={{
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                    </div>
                    {/* MODIFY: Charter Dropdown First */}
                    <div style={{ marginTop: "5px" }}>
                      <h4>Charter:  <span style={{ color: "red" }}>*</span> </h4>
                      {shouldEnableCharterFields ? (
                        <select
                          name="learningCenterCharter"
                          value={selectedCharter}
                          onChange={handleCharterChange}
                          required
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                        >
                          <option value="">Select a Charter</option>
                          {availableCharters.map((charter, index) => (
                            <option key={charter.id || index} value={charter.name}>
                              {charter.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          name="learningCenterCharter"
                          value={formData.learningCenterCharter}
                          onChange={handleChange}
                          disabled
                        />
                      )}
                    </div>

                    {/* MODIFY: Site Dropdown Second */}
                    <div style={{ marginTop: "5px" }}>
                      <h4>Site/Learning Center: <span style={{ color: "red" }}>*</span> </h4>
                      {shouldEnableCharterFields ? (
                        <select
                          name="learningCenterName"
                          value={formData.learningCenterName || ""}
                          onChange={handleSiteChange}
                            required
                          disabled={!selectedCharter}
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            backgroundColor: !selectedCharter ? "#f5f5f5" : "white",
                          
                          }}
                        >
                          <option value="">
                            {!selectedCharter ? "Select a Charter first" : "Select a Site"}
                          </option>
                          {availableSites.map((site, index) => (
                            <option key={site.id || index} value={site.name}>
                              {site.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          name="learningCenterName"
                          value={formData.learningCenterName}
                          onChange={handleChange}
                          disabled
                        />
                      )}
                    </div>

                    {/* MODIFY: Address auto-filled */}
                    <div style={{ marginTop: "5px" }}>
                      <h4>Address:</h4>
                      <input
                        type="text"
                        name="learningCenterAddress"
                        value={formData.learningCenterAddress}
                        onChange={handleChange}
                        disabled
                        style={{
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                    </div>
                  </div>

                  {/* REST OF THE FORM REMAINS UNCHANGED */}
                  <div className="sub-form" style={{ marginTop: "5px" }}>
                    <h3>
                      {formData.age >= 18
                        ? `${formData.studentFirstName}'s`
                        : "Student"}{" "}
                      Information
                    </h3>
                    <div style={{ marginTop: "5px" }}>
                      <h4>
                        Student's First Name:{" "}
                        <span style={{ color: "red" }}>*</span>
                      </h4>
                      <input
                        type="text"
                        name="studentFirstName"
                        value={formData.studentFirstName}
                        onChange={handleChange}
                        maxLength={30}
                        required
                      />
                    </div>
                    <div style={{ marginTop: "5px" }}>
                      <h4>
                        Student's Last Name:{" "}
                        <span style={{ color: "red" }}>*</span>
                      </h4>

                      <input
                        type="text"
                        name="studentLastName"
                        value={formData.studentLastName}
                        onChange={handleChange}
                        maxLength={30}
                        required
                      />
                    </div>
                    <div style={{ marginTop: "5px" }}>
                      <h4>
                        Age: <span style={{ color: "red" }}>*</span>
                      </h4>

                      <input
                        type="text"
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        required
                        style={{
                          border: ageError
                            ? "2px solid #d8302f"
                            : "1px solid #ccc",
                        }}
                      />
                    </div>
                    {ageError && (
                      <div className="error-message" style={{ color: "red" }}>
                        {ageError}
                      </div>
                    )}
                    <div style={{ marginTop: "5px" }}>
                      <h4>
                        Birth Date: <span style={{ color: "red" }}>*</span>
                      </h4>

                      <input
                        type="date"
                        name="birthDate"
                        value={
                          formData.birthDate
                            ? moment(formData.birthDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  {formData.age < 18 && (
                    <>
                      {/* Parent/Guardian Input Form */}
                      <div className="sub-form" style={{ marginTop: "5px" }}>
                        <h3>Parent/Guardian Information</h3>
                        <div style={{ marginTop: "5px" }}>
                          <h4>
                            Your Relationship to {formData.studentFirstName}:{" "}
                            <span style={{ color: "red" }}>*</span>
                          </h4>

                          <input
                            type="text"
                            name="parentRelationship"
                            value={formData.parentRelationship}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div style={{ marginTop: "5px" }}>
                          <h4>
                            Parent First Name:{" "}
                            <span style={{ color: "red" }}>*</span>
                          </h4>

                          <input
                            type="text"
                            name="parentFirstName"
                            value={formData.parentFirstName}
                            onChange={handleChange}
                            maxLength={30}
                            required
                          />
                        </div>
                        <div style={{ marginTop: "5px" }}>
                          <h4>
                            Parent Last Name:{" "}
                            <span style={{ color: "red" }}>*</span>
                          </h4>

                          <input
                            type="text"
                            name="parentLastName"
                            value={formData.parentLastName}
                            onChange={handleChange}
                            maxLength={30}
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="sub-form" style={{ marginTop: "5px" }}>
                    <h3>
                      {" "}
                      {formData.age >= 18
                        ? `${formData.studentFirstName}'s`
                        : `${formData.parentRelationship}'s`}{" "}
                      Contact Information
                    </h3>
                    <div style={{ marginTop: "5px" }}>
                      <h4>
                        Email: <span style={{ color: "red" }}>*</span>
                      </h4>
                      <input
                        type="email"
                        name="primaryEmail"
                        value={formData.primaryEmail}
                        onChange={handleChange}
                        maxLength={60}
                        required
                        style={{
                          border: validateEmail(formData.primaryEmail)
                            ? "1px solid #ccc"
                            : "2px solid #d8302f",
                        }}
                      />
                      {!validateEmail(formData.primaryEmail) && (
                        <p className="validation-msg">
                          Please provide a valid email address.
                        </p>
                      )}
                    </div>
                    <div style={{ marginTop: "5px" }}>
                      <h4>
                        Phone Number: <span style={{ color: "red" }}>*</span>
                      </h4>

                      <input
                        type="text"
                        name="phone"
                        maxLength="12"
                        value={formData.phone}
                        onChange={(e) => {
                          formatPhoneNumber(e);
                          handleChange(e);
                        }}
                        placeholder="XXX-XXX-XXXX"
                        required
                        style={{
                          border: validatePhone(formData.phone)
                            ? "1px solid #ccc"
                            : "2px solid #d8302f",
                        }}
                      />
                      {!validatePhone(formData.phone) && (
                        <p className="validation-msg">
                          Please provide a valid phone number in the format
                          XXX-XXX-XXXX.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="sub-form" style={{ marginTop: "5px" }}>
                    <h3>
                      {formData.age >= 18
                        ? `${formData.studentFirstName}'s`
                        : `${formData.parentRelationship}'s`}{" "}
                      Physical Address
                    </h3>
                    <div style={{ marginTop: "5px" }}>
                      <h4>Street 1:</h4>
                      <input
                        type="text"
                        name="street1"
                        value={formData.street1}
                        onChange={handleChange}
                        maxLength={50}
                      />
                    </div>
                    <div style={{ marginTop: "5px" }}>
                      <h4>Street 2:</h4>
                      <input
                        type="text"
                        name="street2"
                        value={formData.street2}
                        onChange={handleChange}
                        maxLength={40}
                      />
                    </div>
                    <div style={{ marginTop: "5px" }}>
                      <h4>Country:</h4>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        maxLength={25}
                      />
                    </div>

                    <div style={{ marginTop: "5px" }}>
                      <h4>ZIP Code:</h4>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        placeholder="XXXXX"
                        onChange={handleChange}
                        maxLength={5}
                        style={{
                          border: error
                            ? "2px solid #d8302f"
                            : "1px solid #ccc",
                        }}
                      />
                      {error && (
                        <p className="validation-msg" style={{ color: "red" }}>
                          {error}
                        </p>
                      )}
                    </div>

                    <div style={{ marginTop: "5px" }}>
                      <h4>State:</h4>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        maxLength={25}
                      />
                    </div>

                    <div style={{ marginTop: "5px" }}>
                      <h4>City:</h4>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        maxLength={25}
                      />
                    </div>

                    <div style={{ marginTop: "5px" }}>
                      <h4>
                        Same as Mailing Address{" "}
                       {/* <span style={{ color: "red" }}>*</span> */}
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          marginTop: "8px",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="sameAddress"
                            value="true"
                            checked={formData.sameAddress === true}
                            onChange={(e) => {
                              setFormData((prevData) => ({
                                ...prevData,
                                sameAddress: true,
                              }));
                            }}
                            style={{
                              marginRight: "5px",
                              accentColor: "#007bff", // Blue color for the radio button
                              width: "16px",
                              height: "16px",
                            }}
                          />
                          Yes
                        </label>

                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="sameAddress"
                            value="false"
                            checked={formData.sameAddress === false}
                            onChange={(e) => {
                              setFormData((prevData) => ({
                                ...prevData,
                                sameAddress: false,
                              }));
                            }}
                            style={{
                              marginRight: "5px",
                              accentColor: "#007bff", // Blue color for the radio button
                              width: "16px",
                              height: "16px",
                            }}
                          />
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                  {!formData.sameAddress && (
                    <div className="sub-form" style={{ marginTop: "5px" }}>
                      <h3>
                        {formData.age >= 18
                          ? `${formData.studentFirstName}'s`
                          : `${formData.parentRelationship}'s`}{" "}
                        Mailing Address
                      </h3>
                      <div style={{ marginTop: "5px" }}>
                        <h4>Mailing Street 1:</h4>
                        <input
                          type="text"
                          name="mailingStreet1"
                          value={formData.mailingStreet1}
                          onChange={handleChange}
                          maxLength={50}
                        />
                      </div>
                      <div style={{ marginTop: "5px" }}>
                        <h4>Mailing Street 2:</h4>
                        <input
                          type="text"
                          name="mailingStreet2"
                          value={formData.mailingStreet2}
                          onChange={handleChange}
                          maxLength={40}
                        />
                      </div>
                      <div style={{ marginTop: "5px" }}>
                        <h4>Mailing Country:</h4>
                        <input
                          type="text"
                          name="mailingCountry"
                          value={formData.mailingCountry}
                          onChange={handleChange}
                          maxLength={25}
                        />
                      </div>
                      <div style={{ marginTop: "5px" }}>
                        <h4>Mailing ZIP/Postal Code:</h4>
                        <input
                          type="text"
                          name="mailingZipCode"
                          value={formData.mailingZipCode}
                          onChange={handleChange}
                          maxLength={5}
                          placeholder="XXXXX"
                          style={{
                            border: error1
                              ? "2px solid #d8302f"
                              : "1px solid #ccc",
                          }}
                        />
                        {error1 && (
                          <p
                            className="validation-msg"
                            style={{ color: "red" }}
                          >
                            {error1}
                          </p>
                        )}
                      </div>
                      <div style={{ marginTop: "5px" }}>
                        <h4>Mailing State:</h4>
                        <input
                          type="text"
                          name="mailingState"
                          value={formData.mailingState}
                          onChange={handleChange}
                          maxLength={25}
                        />
                      </div>
                      <div style={{ marginTop: "5px" }}>
                        <h4>Mailing City:</h4>
                        <input
                          type="text"
                          name="mailingCity"
                          value={formData.mailingCity}
                          onChange={handleChange}
                          maxLength={25}
                        />
                      </div>
                    </div>
                  )}

                  <button type="submit" className="btn btn-primary" style={{ marginTop: "5px" }}>
                    Save Changes
                  </button>
                </div>
              </form>
            ) : (
              <div>No data available for this entry.</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditInterestForm;